import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { Button, CircularProgress, Icon } from '@material-ui/core';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import Http from '../../../../Http';

const PROTOCOL_OPTIONS = [
  { value: 'SFTP', label: 'SFTP' },
  { value: 'AS2', label: 'AS2' },
  { value: 'API', label: 'API HTTPS' },
];

export const ClienteEdiConfig = ({ match, id: propId, onSave }) => {
  const routeParams = useParams() || {};
  const clientId = propId || match?.params?.id || routeParams?.id;

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const loadLogs = useCallback(async () => {
    if (!clientId) return;
    setLoadingLogs(true);
    try {
      const res = await Http.get(`cliente/${clientId}/edi-logs`);
      if (res && !res.error && res.data?.rows) {
        setLogs(res.data.rows);
      } else {
        setLogs([]);
      }
    } catch (e) {
      console.error('Error al cargar logs EDI', e);
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const loadClientData = useCallback(async () => {
    if (!clientId) return null;
    try {
      const res = await Http.get(`cliente/${clientId}/edi-config`);
      if (res && !res.error && res.data) {
        return {
          protocol: res.data.protocol || 'SFTP',
          host: res.data.host || '',
          port: res.data.port !== undefined && res.data.port !== null ? String(res.data.port) : '22',
          username: res.data.username || '',
          password: res.data.password || '',
          ssh_key: res.data.ssh_key || '',
          inbound_path: res.data.inbound_path || '/INBOUND/',
          archive_path: res.data.archive_path || '/INBOUND/READED/',
          outbound_path: res.data.outbound_path || '/OUTBOUND/',
          cron_expression: res.data.cron_expression || '0 * * * *',
          active: Boolean(res.data.active),
        };
      }
      return {
        protocol: 'SFTP',
        host: '',
        port: '22',
        username: '',
        password: '',
        ssh_key: '',
        inbound_path: '/INBOUND/',
        archive_path: '/INBOUND/READED/',
        outbound_path: '/OUTBOUND/',
        cron_expression: '0 * * * *',
        active: false,
      };
    } catch (e) {
      console.error('Error al cargar configuración EDI', e);
      return null;
    }
  }, [clientId]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await Http.post('cliente/edi-sync', {});
      if (res && !res.error) {
        setSyncMessage({ type: 'success', text: 'Sincronización completada exitosamente' });
        await loadLogs();
      } else {
        setSyncMessage({ type: 'error', text: 'Error al sincronizar: ' + (res?.errMsg || 'Fallo desconocido') });
      }
    } catch (err) {
      setSyncMessage({ type: 'error', text: 'Error de conexión durante sincronización' });
    } finally {
      setSyncing(false);
    }
  };

  const fields = useMemo(
    () => [
      {
        name: 'section_conexion',
        label: 'Configuración de Conexión EDI',
        type: 'section',
        col: 12,
      },
      {
        name: 'protocol',
        label: 'Protocolo de Transporte',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El protocolo es requerido')],
        options: PROTOCOL_OPTIONS,
      },
      {
        name: 'host',
        label: 'Servidor / Host',
        type: 'text',
        col: 6,
        required: true,
        validators: [Validators.required('El host es requerido')],
        placeholder: 'sftp.ejemplo.com',
      },
      {
        name: 'port',
        label: 'Puerto',
        type: 'number',
        col: 4,
        required: true,
        validators: [Validators.required('El puerto es requerido')],
        placeholder: '22',
      },
      {
        name: 'username',
        label: 'Usuario / Username',
        type: 'text',
        col: 4,
        required: true,
        validators: [Validators.required('El usuario es requerido')],
        placeholder: 'edi_user',
      },
      {
        name: 'password',
        label: 'Contraseña (SFTP / AS2)',
        type: 'password',
        col: 4,
        placeholder: '••••••••',
      },
      {
        name: 'ssh_key',
        label: 'Llave Privada SSH (Para Autenticación con Llaves)',
        type: 'textarea',
        col: 12,
        placeholder: '-----BEGIN OPENSSH PRIVATE KEY-----...',
      },
      {
        name: 'section_rutas',
        label: 'Rutas de Archivos en Servidor',
        type: 'section',
        col: 12,
      },
      {
        name: 'inbound_path',
        label: 'Ruta de Entrada (Inbound)',
        type: 'text',
        col: 4,
        placeholder: '/INBOUND/',
      },
      {
        name: 'archive_path',
        label: 'Ruta de Historial / Leídos',
        type: 'text',
        col: 4,
        placeholder: '/INBOUND/READED/',
      },
      {
        name: 'outbound_path',
        label: 'Ruta de Salida (Outbound)',
        type: 'text',
        col: 4,
        placeholder: '/OUTBOUND/',
      },
      {
        name: 'section_cron',
        label: 'Planificación de Procesos Automáticos',
        type: 'section',
        col: 12,
      },
      {
        name: 'cron_expression',
        label: 'Expresión Cron de Sincronización',
        type: 'text',
        col: 6,
        placeholder: '0 * * * *',
        helperText: 'Formato estándar cron (min hora día mes dds)',
      },
      {
        name: 'active',
        label: 'Módulo EDI Activo para este Cliente',
        type: 'checkbox',
        col: 6,
      },
    ],
    []
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const payload = {
          ...values,
          id_cliente: clientId,
        };
        const res = await Http.post(`cliente/${clientId}/edi-config`, payload);
        return res;
      } catch (err) {
        return { error: true, errMsg: 'Error al guardar configuración EDI' };
      }
    },
    [clientId]
  );

  const handleSuccess = useCallback(
    (response) => {
      if (onSave) {
        onSave(response);
      }
    },
    [onSave]
  );

  const headerActions = (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      onClick={handleSync}
      disabled={syncing}
      startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Icon>sync</Icon>}
      className="!capitalize"
    >
      {syncing ? 'Sincronizando...' : 'Sincronizar Inbound'}
    </Button>
  );

  return (
    <div className="space-y-4">
      {syncMessage && (
        <div
          className={`p-3 rounded-lg text-xs font-medium flex items-center justify-between ${
            syncMessage.type === 'error'
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          }`}
        >
          <span>{syncMessage.text}</span>
          <button
            type="button"
            onClick={() => setSyncMessage(null)}
            className="text-gray-400 hover:text-gray-600 font-bold"
          >
            ×
          </button>
        </div>
      )}

      <CatalogForm
        title="Configuración EDI"
        subtitle="Conexión segura de transferencia SFTP/AS2 y automatización de archivos"
        fields={fields}
        id={clientId}
        loadData={loadClientData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        headerActions={headerActions}
        submitButtonText="Guardar Conexión EDI"
      />

      {/* Bitácora de Transacciones EDI */}
      <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-4">
        <div className="flex items-center justify-between pb-2 mb-3 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-800 tracking-tight">Bitácora de Transacciones EDI</h3>
            <p className="text-xs text-gray-500">Historial reciente de intercambios de archivos procesados</p>
          </div>
          <Button
            size="small"
            variant="text"
            onClick={loadLogs}
            disabled={loadingLogs}
            startIcon={<Icon>refresh</Icon>}
            className="!text-xs !capitalize"
          >
            Actualizar Bitácora
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-gray-50/75 text-gray-600 font-semibold uppercase tracking-wider text-[10px] border-b border-gray-200">
              <tr>
                <th className="py-2 px-3">Archivo</th>
                <th className="py-2 px-3">Dirección</th>
                <th className="py-2 px-3">Tipo</th>
                <th className="py-2 px-3">Estado</th>
                <th className="py-2 px-3">Detalle / Error</th>
                <th className="py-2 px-3 text-right">Fecha Procesado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingLogs ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-gray-400">
                    <CircularProgress size={20} />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-gray-400">
                    No se han registrado transacciones para este cliente.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/50">
                    <td className="py-2 px-3 font-medium text-gray-800">{log.filename || 'N/A'}</td>
                    <td className="py-2 px-3 text-gray-600">{log.direction}</td>
                    <td className="py-2 px-3 text-gray-600">{log.transaction_type}</td>
                    <td className="py-2 px-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          log.status === 'PROCESSED'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : log.status === 'FAILED'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                      >
                        {log.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-red-600 max-w-xs truncate" title={log.error_message || ''}>
                      {log.error_message || '—'}
                    </td>
                    <td className="py-2 px-3 text-gray-500 text-right whitespace-nowrap">
                      {log.processed_at ? new Date(log.processed_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default withRouter(ClienteEdiConfig);
